/**
 * CheckInCountsModal — shown when checking in a registrant who registered a party
 * of more than one. Pre-fills the actual member/non-member headcount with the
 * registered numbers so staff can confirm or correct what actually showed up
 * (registered-vs-actual verification).
 */
import React, { useState } from "react";
import Button from "../common/Button";

interface CheckInCountsModalProps {
  name: string;
  registeredMembers: number;
  registeredNonMembers: number;
  busy?: boolean;
  onConfirm: (counts: { memberCount: number; nonMemberCount: number }) => void;
  onCancel: () => void;
}

const clamp = (n: number) => (Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0);

const CheckInCountsModal: React.FC<CheckInCountsModalProps> = ({
  name,
  registeredMembers,
  registeredNonMembers,
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const [members, setMembers] = useState(String(registeredMembers));
  const [nonMembers, setNonMembers] = useState(String(registeredNonMembers));

  const m = clamp(Number(members));
  const n = clamp(Number(nonMembers));
  const registeredTotal = registeredMembers + registeredNonMembers;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Confirm attendance
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          <strong>{name}</strong> registered a party of {registeredTotal} (
          {registeredMembers} member{registeredMembers === 1 ? "" : "s"},{" "}
          {registeredNonMembers} non-member
          {registeredNonMembers === 1 ? "" : "s"}). Enter how many actually
          attended.
        </p>
        <div className="grid grid-cols-2 gap-4 mb-5">
          <label className="block text-sm">
            <span className="text-gray-700">Members present</span>
            <input
              type="number"
              min="0"
              value={members}
              onChange={(e) => setMembers(e.target.value)}
              className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700">Non-members present</span>
            <input
              type="number"
              min="0"
              value={nonMembers}
              onChange={(e) => setNonMembers(e.target.value)}
              className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </label>
        </div>
        <p className="text-xs text-gray-500 mb-4">Actual total: {m + n}</p>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onConfirm({ memberCount: m, nonMemberCount: n })}
            disabled={busy}
          >
            {busy ? "Checking in…" : "Confirm check-in"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CheckInCountsModal;
